import { useEffect, useState } from 'react';
import { TaxGroup, TaxGroupInput, TaxGroupResponse } from '@/types/schemas/nhom-thue.type';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseTaxGroupReturn {
  taxGroups: TaxGroup[];
  isLoading: boolean;
  addTaxGroup: (newTaxGroup: TaxGroupInput) => Promise<TaxGroup>;
  updateTaxGroup: (uuid: string, updatedTaxGroup: TaxGroupInput) => Promise<TaxGroup>;
  deleteTaxGroup: (uuid: string) => Promise<void>;
  refreshTaxGroups: (groupType?: string) => Promise<void>;
  fetchTaxGroupsByType: (groupType: string) => Promise<void>;
}

export const useTaxGroup = (initialTaxGroups: TaxGroup[] = []): UseTaxGroupReturn => {
  const [taxGroups, setTaxGroups] = useState<TaxGroup[]>(initialTaxGroups);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchTaxGroups = async (groupType?: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const url = groupType
        ? `/entities/${entity.slug}/erp/groups/?loai_nhom=${groupType}`
        : `/entities/${entity.slug}/erp/groups/`;

      const response = await api.get<TaxGroupResponse>(url);
      setTaxGroups(response.data.results);
    } catch (error) {
      console.error('Error fetching tax groups:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTaxGroupsByType = async (groupType: string) => {
    await fetchTaxGroups(groupType);
  };

  const addTaxGroup = async (newTaxGroup: TaxGroupInput): Promise<TaxGroup> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const payload = {
        ma_nhom: newTaxGroup.ma_nhom,
        ten_phan_nhom: newTaxGroup.ten_phan_nhom,
        ten2: newTaxGroup.ten2,
        trang_thai: newTaxGroup.trang_thai,
        loai_nhom: newTaxGroup.loai_nhom || 'TAX'
      };

      const response = await api.post<TaxGroup>(`/entities/${entity.slug}/erp/groups/`, payload);
      const addedTaxGroup = response.data;

      setTaxGroups(prev => [...prev, addedTaxGroup]);
      return addedTaxGroup;
    } catch (error) {
      console.error('Error adding tax group:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateTaxGroup = async (uuid: string, updatedTaxGroup: TaxGroupInput): Promise<TaxGroup> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const payload = {
        ma_nhom: updatedTaxGroup.ma_nhom,
        ten_phan_nhom: updatedTaxGroup.ten_phan_nhom,
        ten2: updatedTaxGroup.ten2,
        trang_thai: updatedTaxGroup.trang_thai,
        loai_nhom: updatedTaxGroup.loai_nhom || 'TAX'
      };

      const response = await api.put<TaxGroup>(`/entities/${entity.slug}/erp/groups/${uuid}/`, payload);
      const updatedTaxGroupData = response.data;

      setTaxGroups(prev =>
        prev.map(taxGroup => (taxGroup.uuid === updatedTaxGroupData.uuid ? updatedTaxGroupData : taxGroup))
      );

      return updatedTaxGroupData;
    } catch (error) {
      console.error('Error updating tax group:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTaxGroup = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/groups/${uuid}/`);
      setTaxGroups(prev => prev.filter(taxGroup => taxGroup.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting tax group:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshTaxGroups = async (groupType?: string): Promise<void> => {
    await fetchTaxGroups(groupType);
  };

  useEffect(() => {
    fetchTaxGroups();
  }, [entity?.slug]);

  return {
    taxGroups,
    isLoading,
    addTaxGroup,
    updateTaxGroup,
    deleteTaxGroup,
    refreshTaxGroups,
    fetchTaxGroupsByType
  };
};
